import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Loader2, ArrowLeft } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { useI18n } from "../lib/i18n";
import { useSEO } from "../lib/useSEO";
import api from "../lib/api";

export default function AdminLogin() {
  const { t, lang } = useI18n();
  useSEO({ titleKey: "seo.admin_title", descriptionKey: "seo.admin_desc" });
  const { user, login, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState("signin"); // signin | forgot | sent
  const navigate = useNavigate();
  const logoSrc = lang === "pl" ? "/brand/logo-pl.png" : "/brand/logo-en.jpg";

  if (!loading && user) return <Navigate to="/admin" replace />;

  const handleLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(typeof detail === "string" ? detail : "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setView("sent");
    } catch (err) {
      // Backend always returns 200 — still defend the UI.
      setView("sent");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen grid place-items-center px-5 bg-water-grad"
      data-testid="admin-login-page"
    >
      <div className="w-full max-w-md rounded-3xl bg-white border border-slate-200 shadow-xl p-8">
        <div className="flex items-center justify-center">
          <img
            src={logoSrc}
            alt="LocalH2O"
            className="h-32 w-auto select-none"
            draggable={false}
          />
        </div>

        {view === "signin" && (
          <form onSubmit={handleLogin}>
            <div className="mt-2 text-center">
              <div className="text-xs text-slate-500">{t("admin.login")}</div>
            </div>
            <div className="mt-7 space-y-4">
              <div>
                <Label>{t("admin.email")}</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="admin-login-email"
                />
              </div>
              <div>
                <Label>{t("admin.password")}</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="admin-login-password"
                />
              </div>
              <button
                type="submit"
                disabled={busy}
                className="w-full btn-primary inline-flex items-center justify-center gap-2"
                data-testid="admin-login-submit"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                {t("admin.sign_in")}
              </button>
              <div className="text-center pt-1">
                <button
                  type="button"
                  onClick={() => setView("forgot")}
                  className="text-sm text-sky-700 hover:text-sky-900 font-semibold"
                  data-testid="forgot-password-link"
                >
                  {t("admin.forgot_password_link")}
                </button>
              </div>
            </div>
          </form>
        )}

        {view === "forgot" && (
          <form onSubmit={handleForgot} data-testid="forgot-password-form">
            <div className="mt-2 text-center">
              <h2 className="font-display text-xl font-bold tracking-tight">
                {t("admin.forgot_password_title")}
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                {t("admin.forgot_password_subtitle")}
              </p>
            </div>
            <div className="mt-6 space-y-4">
              <div>
                <Label>{t("admin.email")}</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  data-testid="forgot-password-email"
                />
              </div>
              <button
                type="submit"
                disabled={busy}
                className="w-full btn-primary inline-flex items-center justify-center gap-2"
                data-testid="forgot-password-submit"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                {t("admin.forgot_password_submit")}
              </button>
              <div className="text-center pt-1">
                <button
                  type="button"
                  onClick={() => setView("signin")}
                  className="text-sm text-slate-600 hover:text-slate-900 font-semibold inline-flex items-center gap-1"
                  data-testid="forgot-password-back"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  {t("admin.forgot_password_back")}
                </button>
              </div>
            </div>
          </form>
        )}

        {view === "sent" && (
          <div className="text-center mt-6" data-testid="forgot-password-sent">
            <div className="w-14 h-14 rounded-full bg-emerald-100 grid place-items-center mx-auto">
              <svg className="w-7 h-7 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h2 className="mt-4 font-display text-xl font-bold tracking-tight">
              {t("admin.forgot_password_title")}
            </h2>
            <p className="text-sm text-slate-600 mt-2 px-2">
              {t("admin.forgot_password_sent")}
            </p>
            <button
              type="button"
              onClick={() => {
                setView("signin");
                setEmail("");
              }}
              className="mt-6 inline-flex items-center gap-1 text-sky-700 hover:text-sky-900 font-semibold"
              data-testid="forgot-sent-back"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              {t("admin.forgot_password_back")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
